import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, FeedbackBanner, Panel } from '@usacow/ds';
import { api, type Postmortem as PM } from '../lib/api';

const KIND_COLOR: Record<string, string> = {
  contest_start: 'var(--text-tertiary)',
  open: 'var(--text-tertiary)',
  submit: 'var(--correct)',
  lock: 'var(--text-secondary)',
};

function fmt(t: number) {
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function Postmortem() {
  const navigate = useNavigate();
  const [pm, setPm] = useState<PM | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api
      .contestPostmortem()
      .then(setPm)
      .catch(() => setMissing(true));
  }, []);

  if (missing) {
    return (
      <div className="scr scr--narrow">
        <div className="scr-head">
          <div>
            <div className="t-eyebrow">postmortem</div>
            <h1 className="scr-h">No contest yet</h1>
          </div>
        </div>
        <p className="dim">Run a contest simulation to get a postmortem of your reasoning.</p>
        <Button
          variant="primary"
          style={{ marginTop: 14 }}
          onClick={() => navigate('/app/contest')}
        >
          Start a contest
        </Button>
      </div>
    );
  }
  if (!pm) return <div className="route-loading">Loading…</div>;

  const submitted = pm.problems.filter((p) => p.status === 'submitted');
  const ac = submitted.filter((p) => (p.score ?? 0) >= 1000).length;

  return (
    <div className="scr scr--narrow">
      <div className="scr-head">
        <div>
          <div className="t-eyebrow">postmortem · {pm.division} mock</div>
          <h1 className="scr-h">What your reasoning did</h1>
        </div>
        <Badge tone={pm.score >= 700 ? 'correct' : 'partial'}>
          {ac}/{pm.problems.length} AC
        </Badge>
      </div>

      <FeedbackBanner
        state={pm.score >= 700 ? 'correct' : 'warning'}
        title={`Scored ${Math.round(pm.score / 10)} / 100.`}
        slug={`${submitted.length} submitted of ${pm.problems.length}`}
      >
        {pm.score >= 700
          ? 'Solid recognition under time. Keep the constraint-first habit.'
          : 'The misses cluster where attempts at this level usually go wrong — those drills are scheduled for repair.'}
      </FeedbackBanner>

      <Panel title="Decision timeline" style={{ marginTop: 14 }}>
        <div className="pm-tl">
          {pm.timeline.map((e, i) => (
            <div className="pm-tl__row" key={i}>
              <span className="pm-tl__t mono">{fmt(e.t)}</span>
              <span
                className="pm-tl__dot"
                style={{ background: KIND_COLOR[e.type] ?? 'var(--text-tertiary)' }}
              />
              <span className="pm-tl__e">
                {e.type.replace('_', ' ')}
                {e.problem ? ` · ${e.problem}` : ''}
              </span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Per-problem" style={{ marginTop: 14 }}>
        <div className="gp-steps">
          {pm.problems.map((p) => (
            <div className="gp-steprow" key={p.id}>
              <span className="mono">{p.id}</span>
              <Badge
                tone={
                  p.status !== 'submitted'
                    ? 'neutral'
                    : (p.score ?? 0) >= 1000
                      ? 'correct'
                      : 'partial'
                }
                variant="outline"
              >
                {p.status !== 'submitted'
                  ? 'not submitted'
                  : (p.score ?? 0) >= 1000
                    ? 'AC'
                    : `${Math.round((p.score ?? 0) / 10)}%`}
              </Badge>
            </div>
          ))}
        </div>
      </Panel>

      <div className="gp-finish">
        <Button variant="primary" size="lg" onClick={() => navigate('/app/repair')}>
          Start repair
        </Button>
      </div>
    </div>
  );
}
